/**
 * 점검 자료가 아닌 목록(직원·현장·거래처별 수치)의 보고 문서 표. 코드가 센다. 모델은 이것을 근거로 문장만 쓴다.
 *
 * 연차 촉진 대상자 명단(실측 2026-10-26 14:00): 1턴 표 74명·12칸이 2턴 공지와 3턴 발표자료에 한 줄도 안 실렸다. 문서 표는
 * 점검 자료(〈표 1〉 처리 현황, 〈표 2〉 경과)만 코드가 만들었고, 그 밖의 표는 「한 장」이면 통째로 뺐다. 그림은 첫 수 칸
 * (연차생성)을 골라 촉진과 상관없는 막대를 그렸다.
 */
import type { ReportChart, TableData } from './files';
import { subjectParticle, topicParticle } from './korean';
import { dayLabel } from './reportData';

/** 문서 표 하나. groups는 묶음(부서)별 현황, top은 주 수치 상위 행(사람 이름이 든다). */
export type ListTable = { role: 'groups' | 'top'; caption: string; table: TableData };

export type ListReport = {
  tables: ListTable[];
  /** 묶음별 주 수치 막대. 묶음 칸이 없으면 상위 이름별 막대(공지에는 없다). */
  chart?: ReportChart;
  /** 묶음 그림의 결론(상위 3개 묶음의 합과 몫). 모델이 그림 결론을 안 쓰면 이것이 발표자료 그림 장의 제목이 된다. */
  chartLine?: string;
  /** 보고 문서 근거: 모델이 옮길 숫자. */
  facts: string[];
  /** 요약 자리의 핵심 숫자. */
  figures: { label: string; value: string }[];
  /** 확인 필요 사항(코드가 센다): 연차가 아닌 사람 목록의 숫자가 붙은 이름. 사람 이름은 싣지 않는다. */
  checks: string[];
  /** 연차·휴가 목록인가(수 칸 이름). 연차 보고 문서의 확인 줄(leaveNotes)을 붙일지 정한다. */
  leave: boolean;
  /** 대상 확정이 필요한 묶음의 짧은 이름(「이름에 숫자가 붙은 2명」). 보고서 조치의 첫 줄에 쓴다. */
  confirm: string[];
  /** 연차 목록에서 대상에서 뺀 계정의 줄(이름에 숫자가 붙은 계정). 보고서 요약 끝에 코드가 싣는다. */
  excluded?: string;
  /** 대상에서 뺀 계정의 이름. 엑셀 명단에 대상 여부를 밝힐 때 쓴다. */
  excludedNames?: string[];
  /** 합계 줄을 뺀 행을 주 수치 많은 순으로(같으면 표 차례). 1턴 답의 표 미리보기가 이 차례로 본다. */
  ranked: unknown[][];
  /** 주 수치 칸의 문서 말(「연차 잔여」). */
  mainLabel: string;
  /** 이름 칸의 이름(「사원명」). */
  nameColumn: string;
  /** 대상 행 수(뺀 계정을 뺀 수). */
  count: number;
  /** 묶음 값에 임원(「임원」, 「임원실」, 「임원진」)이 있는가. 공지의 대상은 임직원이 된다. */
  executives: boolean;
  /** 주 수치가 남은 양(잔여·잔액·미○○) 칸인가. */
  remaining: boolean;
};

/**
 * 칸 이름을 문서 말로(「연차잔여일수」를 「연차 잔여」로). 1턴 칸 이름을 그대로 옮긴 「연차잔여일수 상위 9명」이 나갔다(국가직 팀장
 * 재검토). 계열 칸이 여럿이면 앞말(연차)로 이미 가른다.
 */
function humanLabel(name: string): string {
  // 띄어 쓴 「연차 잔여일수」도 같다(실측 18:12).
  const found = /^(연차|대체|휴가|총)(잔여|사용|생성|발생|근무)(?:일수)?$/.exec(name.replace(/\s+/g, ''));
  return found ? `${found[1]} ${found[2]}` : name;
}

/** 사용한 양 칸(생성 대비 사용). 잔여 칸과 가른다. */
const USED = /사용|실적|완료|수금|처리|소진/;

/** 연차 생성 기준: 입사일(hired), 회계년도(fiscal), 모름(undefined). */
export type LeaveBasis = 'hired' | 'fiscal' | undefined;

/**
 * 연차 생성 기준. context는 계산 요약과 조회 조건(화면 글자)이다. 생성 기준 칸의 값으로만 본다. 다른 칸의 「입사일」로 정하지 않는다
 * (독립 재검토: 생성 기준이 회계년도인데 입사일 줄이 붙었다).
 */
export function leaveBasis(context: string): LeaveBasis {
  if (/생성\s*기준["'\s:=]*회계/.test(context)) return 'fiscal';
  return /생성\s*기준["'\s:=]*입사일/.test(context) ? 'hired' : undefined;
}

/** 연차 생성 기준이 입사일인가. */
export function hiredBasis(context: string): boolean {
  return leaveBasis(context) === 'hired';
}

/**
 * 연차 보고 문서의 확인 줄. 사실이라 사용자 답을 기다리지 않고 싣는다(국가직 팀장 재검토). 생성 기준이 입사일이면 사용 기간이
 * 직원마다 다르다. 이월 여부와 촉진 요건 줄은 싣지 않는다(사용자 확인, 2026-10-27: 생성 25일 초과는 창립 때부터 일한 직원이고,
 * 촉진 요건은 조치로 쓴다). due는 만료일 확인의 기한과 담당이다(촉진 보고서, 국가직 팀장 검토).
 */
export function leaveNotes(context: string, due?: string): string[] {
  return hiredBasis(context) ? [`연차 생성 기준이 입사일이라 직원마다 사용 기간의 끝이 다름, 개인별 만료일 확인 필요${due ? `(${due})` : ''}`] : [];
}

/** 달을 더한다. 그 달에 없는 날이면 그 달 말일이다(8월 31일의 6개월 전은 2월 말일). */
function addMonths(date: Date, months: number): Date {
  const first = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  return new Date(first.getFullYear(), first.getMonth(), Math.min(date.getDate(), last));
}

function plusDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function dayOf(date: Date): number {
  return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
}

/** 사용 기간이 end에 끝나는 직원의 제61조 서면 촉구 기간: 끝 다음 날의 6개월 전 날부터 10일(12월 31일에 끝나면 7월 1일~10일). */
function urgeWindow(end: Date): { from: Date; to: Date } {
  const from = addMonths(plusDays(end, 1), -6);
  return { from, to: plusDays(from, 9) };
}

/**
 * 서면 촉구 기간이 오늘이나 그 뒤에 끝나는 첫 사용 기간 끝 날. 이보다 먼저 끝나는 직원은 촉구 기간이 지났다. 하루씩 센다.
 * 「6개월 뒤 달에서 10일을 뺀」 셈은 오늘이 그달 10일 이하일 때 하루에서 사흘 틀렸다(국가직 팀장 검토).
 */
function urgeCutoff(today: Date): Date {
  let end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  while (dayOf(urgeWindow(end).to) < dayOf(today)) end = plusDays(end, 1);
  return end;
}

/** from부터 세어, 서면 촉구 기간이 soon까지 열리는 마지막 사용 기간 끝 날. */
function urgeOpenUntil(from: Date, soon: Date): Date {
  let end = from;
  while (dayOf(urgeWindow(plusDays(end, 1)).from) <= dayOf(soon)) end = plusDays(end, 1);
  return end;
}

/**
 * 연차 사용 촉진(근로기준법 제61조)의 조치와 확인 줄. 사용자가 보고서 조치에 넣기로 했다(2026-10-27). 문구는 법대로 「서면 촉구」,
 * 「촉구를 받은 날부터」다(독립 검토). 입사일 기준이면 사용 기간의 끝이 직원마다 달라 시점으로 쓰고, 서면 촉구 시점이 이미 지난
 * 범위와 지금부터 soon(다음 주 마지막 근무일)까지 촉구 기간이 열리는 범위를 밝힌다(국가직 팀장 검토: '27.4. 중순 전에 끝나는 직원은
 * 첫 단계가 지났고, 그 뒤 직원은 촉구가 급한데 계획에 드러나지 않았다). 회계년도 기준이면 자료의 해로 날짜를 셈하고, 지난 단계는
 * 조치 대신 확인 줄로 낸다(독립 검토: 7월 촉구 기간이 지났는데 날짜 없는 조치로 나갔다). 1년 미만 근로자는 시점이 다르다(제61조
 * 제2항).
 */
export function leaveLaw(input: { owner: string; basis: LeaveBasis; today: Date; year?: number; soon?: Date }): {
  actions: string[]; checks: string[];
} {
  const { owner, basis, today } = input;
  const year = input.year ?? today.getFullYear();
  const urge = '직원별 미사용 일수를 알리고 사용 시기를 정해 통보하도록 서면 촉구';
  const assign = '촉구를 받은 날부터 10일 안에 사용 시기를 통보하지 않은 미사용 일수의 사용 시기 서면 지정 통보';
  const young = '입사 1년 미만 직원은 촉진 시점이 다름(제61조 제2항: 최초 1년이 끝나기 3개월 전부터 10일 안 서면 촉구, 1개월 전까지'
    + ' 서면 지정), 해당자 확인 필요';
  if (basis !== 'fiscal') {
    const cutoff = urgeCutoff(today);
    // 지금 촉구 기간 중인 직원만 즉시다. 다음 주까지 기간이 열리는 직원은 열리는 대로다. 기간 전 촉구는 요건을 채우지 못할 수 있다
    // (독립 재검토: 아직 열리지 않은 직원까지 「즉시」로 묶었다).
    const open = urgeOpenUntil(cutoff, today);
    const until = basis === 'hired' && input.soon ? urgeOpenUntil(cutoff, input.soon) : undefined;
    const later = until && dayOf(until) > dayOf(open) ? `, ${dayLabel(plusDays(open, 1))}~${dayLabel(until)}인 직원은 기간이 열리는 대로` : '';
    const urgent = until ? `, 끝나는 날이 ${dayLabel(cutoff)}~${dayLabel(open)}인 직원은 지금 촉구 기간이라 만료일 확인 즉시${later}` : '';
    // 지난 범위는 그때 이미 촉구한 회사에는 맞지 않는 말이라 조건을 붙인다(독립 재검토). 경계는 날마다 움직여 기준일을 단다.
    return {
      actions: [`${urge}(근로기준법 제61조, 사용 기간 끝나기 6개월 전부터 10일 안${urgent}, ${owner})`,
        `${assign}(사용 기간 끝나기 2개월 전까지, ${owner})`],
      checks: [...(basis === 'hired' ? [`사용 기간이 ${dayLabel(cutoff)} 전에 끝나는 직원은 제61조 서면 촉구 시점이 이미 지나, 그때`
        + ` 촉구하지 않았으면 이번 사용 기간에는 수당 면제 요건을 채울 수 없음(${dayLabel(today)} 기준)`] : []), young],
    };
  }
  // 회계년도: 사용 기간이 12월 31일에 끝난다. 서면 촉구는 7월 1일부터 10일 안, 시기 지정 통보는 10월 31일까지다.
  const from = new Date(year, 6, 1);
  const to = new Date(year, 6, 10);
  const due = new Date(year, 9, 31);
  if (dayOf(today) <= dayOf(to)) {
    return {
      actions: [`${urge}(근로기준법 제61조, ${dayLabel(from)}~${dayLabel(to)}, ${owner})`, `${assign}(${dayLabel(due)}까지, ${owner})`],
      checks: [young],
    };
  }
  if (dayOf(today) <= dayOf(due)) {
    return {
      actions: [`7월 ${assign.replace('촉구를', '서면 촉구를')}(근로기준법 제61조, ${dayLabel(due)}까지, ${owner})`],
      checks: [`회계년도 기준 제61조 서면 촉구 기간(${dayLabel(from)}~${dayLabel(to)})이 지났음, 그때 촉구하지 않았으면 이번 사용`
        + ' 기간에는 수당 면제 요건을 채울 수 없음', young],
    };
  }
  return {
    actions: [],
    checks: [`회계년도 기준 ${year}년 제61조 촉진 기한(서면 촉구 ${dayLabel(to)}, 사용 시기 지정 ${dayLabel(due)})이 지났음, 미사용 연차`
      + ' 수당 처리 확인 필요', young],
  };
}

/** 상위 명단 행 수. */
const TOP = 10;
/** 묶음 표 줄 수. 넘으면 끝을 「그 밖」 한 줄로 합친다. 공지 한 장에 부서 스물다섯은 들어간다(부서장이 제 부서를 찾는다). */
const GROUP_ROWS = 25;
/** 그림 막대 수. 넘으면 위 것만 그리고 제목에 밝힌다. 「그 밖」을 합친 막대가 가장 길어 눈을 끌었다(실측 표로 그린 판). */
const CHART_BARS = 12;
/** 공지에 따로 싣는 묶음의 최소 인원. */
const SMALL_GROUP = 3;

type Shape = {
  name: number;
  /** 묶음 칸. 없으면 -1. */
  group: number;
  main: number;
  /** 주 수치와 같은 계열(앞 두 글자가 같은) 수 칸. 표에 함께 싣는다. 주 수치가 든다. */
  measures: number[];
  /** 행 하나의 셈 단위(사람 명, 곳, 건). */
  unitWord: string;
};

function text(cell: unknown): string {
  return cell === null || cell === undefined ? '' : String(cell).replace(/\s+/g, ' ').trim();
}

function numberOf(cell: unknown): number | undefined {
  if (typeof cell === 'number') return Number.isFinite(cell) ? cell : undefined;
  const plain = text(cell).replace(/,/g, '');
  return /^-?\d+(?:\.\d+)?$/.test(plain) ? Number(plain) : undefined;
}

/** 0.5 단위 합계가 부동소수 꼬리를 달지 않게. */
function exact(value: number): number {
  return Math.round(value * 100) / 100;
}

function shown(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

function percent(part: number, whole: number): number {
  return whole ? Math.round((part / whole) * 1000) / 10 : 0;
}

/** 코드 칸: 이름이 코드·번호이거나 값이 0으로 시작하는 숫자 글자(사원코드 00001). 수로 더하지 않는다. */
function codeLike(name: string, cells: unknown[]): boolean {
  if (/코드|번호|사번|^id$|_id$|^no$/i.test(name)) return true;
  const filled = cells.map(text).filter(Boolean);
  return filled.length > 0 && filled.every((value) => /^\d+$/.test(value)) && filled.some((value) => /^0\d/.test(value));
}

/** 시간 칸: 달·날짜 값이거나 이름이 기간을 말하는 칸. 시간 흐름은 그림(선)이 맡는다. */
function timeLike(name: string, cells: unknown[]): boolean {
  if (/월$|월별|일자|날짜|기간|년도|연도|년월/.test(name)) return true;
  const filled = cells.map(text).filter(Boolean);
  return filled.length > 0 && filled.every((value) => /^(?:19|20)\d{2}[-./]?(?:0[1-9]|1[0-2])(?:[-./]?\d{2})?$/.test(value)
    || /^\d{1,2}월$/.test(value));
}

/** 글자 두 개씩 묶음. */
function pairs(value: string): string[] {
  const plain = value.replace(/[\s·,.()%"'{}:<>=_-]/g, '');
  return Array.from(new Set(Array.from({ length: Math.max(plain.length - 1, 0) }, (_, index) => plain.slice(index, index + 2))));
}

/**
 * 문서가 무엇을 다루는지. asked는 사용자가 한 말(질문·과제 목표), said는 우리 쪽 글(직전 답·계산 요약). 계산 요약은
 * 모든 칸의 합계를 담아 칸을 가려 주지 못한다. 「연차생성 합계」가 있어 연차생성이 골렸다(실측 표, 2026-10-26).
 */
export type ListFocus = { asked: string; said?: string };

/** 글이 그 칸을 얼마나 말했나: 칸 이름 글자 묶음이 든 몫, 이름이 통째로 들면 1 더. */
function textScore(name: string, focus: string): number {
  const flat = focus.replace(/\s+/g, '');
  const own = pairs(name);
  if (!own.length) return 0;
  return own.filter((pair) => flat.indexOf(pair) >= 0).length / own.length + (flat.indexOf(name.replace(/\s+/g, '')) >= 0 ? 1 : 0);
}

/** 사용자 말을 두 배로 친다. */
function focusScore(name: string, focus: ListFocus): number {
  return 2 * textScore(name, focus.asked) + textScore(name, focus.said ?? '');
}

/** 남은 것을 묻는 말과 남은 양 칸. 「연차 촉진 대상자」의 주 수치는 생성 칸보다 잔여 칸이다. */
const REMAINING_ASK = /촉진|소진|남은|남아|잔여|미사용|미처리|미수|미완료|미실시|미결/;
const REMAINING = /잔여|잔액|^미/;

/** 모집단을 가른 칸(계산 요약의 「연차잔여일수 > 0」). 그 칸이 목록의 주 수치다. */
function criterion(numbers: number[], columns: string[], said: string): number | undefined {
  const flat = said.replace(/\s+/g, '');
  return numbers.find((index) => {
    const name = (columns[index] ?? '').replace(/\s+/g, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return !!name && new RegExp(`${name}(?:>=?|<=?|초과|이상|미만|이하|[이가]0)`).test(flat);
  });
}

/** 같은 점수면 금액·잔여 칸을 먼저 고른다(「거래처별 매출」의 주 수치는 수량보다 금액, 「미수」는 건수보다 금액이다, 독립 검토). */
const PREFERRED = /금액|잔여|잔액/;

function mainMeasure(numbers: number[], columns: string[], focus: ListFocus): number {
  const score = (index: number) => focusScore(columns[index] ?? '', focus) + (PREFERRED.test(columns[index] ?? '') ? 0.001 : 0);
  const best = (list: number[]) => list.reduce((found, index) => (score(index) > score(found) ? index : found), list[0]!);
  const ruled = criterion(numbers, columns, focus.said ?? '');
  if (ruled !== undefined) return ruled;
  const remaining = REMAINING_ASK.test(focus.asked) ? numbers.filter((index) => REMAINING.test(columns[index] ?? '')) : [];
  return best(remaining.length ? remaining : numbers);
}

const NAME = /명$|성명|이름|사원|직원|담당자|현장|거래처|품목|고객|업체|매장/;
/** 묶음 칸으로 고르는 차례. 부서가 직위보다 먼저다(직위 칸이 앞에 있던 판이 「직위별 연차 현황」으로 묶었다, 실측 2026-10-26 14:35). */
const GROUPS = [/부서|팀|부문/, /현장|지점|사업장|지역/, /구분|유형|분류/, /담당/, /직위|직급/];
const PEOPLE = /사원|성명|이름|직원|담당자|사람|인원|사용자|기사/;
const PLACES = /현장|거래처|지점|업체|고객|매장|사업장/;
/** 더해 봐야 뜻이 없는 수 칸(비율, 평균, 단가, 순번, 나이, 근속). 「연차 사용률 합계 203.3일」이 나왔다(독립 검토). */
const NOT_SUMMED = /율|률|%|비율|비중|평균|단가|순번|번호|^no\.?$|id$|나이|연령|근속/i;
/**
 * 계산 표가 단 합계 줄. 사람 한 명으로 세면 1위가 「합계」가 되고 전체 합이 두 배가 된다(독립 검토). 「총 13명」, 「합계(13명)」 꼴도
 * 합계 줄이다(독립 검토: 숫자가 붙은 계정으로 빠졌다).
 */
const TOTAL_ROW = /^(?:합계|총계|소계|계|전체)$|^(?:합계|총계|소계|총|계)\s*[\d(:：]/;

/** 표의 합계 줄 값인가(「합계」, 「총 13명」). 엑셀 명단의 대상 여부를 비운다. */
export function isTotalRow(cell: unknown): boolean {
  return TOTAL_ROW.test(text(cell));
}

/**
 * 사람 이름 칸(사원명, 성명, 담당자, 사용자명, 담당기사 등). 부서·팀·현장 칸은 아니다. 「사용자구분」, 「기사등급」, 「직원구분」처럼
 * 사람을 가르는 칸은 아니다(독립 재검토: 「관리자」가 이름으로 지워졌다).
 */
const PERSON_COLUMN = /사원명|성명|이름|^직원명?$|^담당자?$|담당자명|작성자|처리자|신청자|점검자|작업자|^사원$|^사용자명?$|(?:^|담당|점검|작업)기사명?$/;

/** 흔한 낱말과 같은 두 글자. 이런 이름은 낱말 속(「10일 이상 보유자」)에서 이름으로 보지 않는다(독립 재검토). */
const COMMON_WORD = /^(?:이상|이하|이후|이전|이내|이외|조정|유지|정기|조기|연말|연초|전체|일부|상위|하위|우선|합계|평균|현황|예정|완료|진행|변경|추가|일반|관리|기준|대상|기한|정도|사용|잔여|안내|요청|제출|확인)$/;
/** 이름 뒤에 붙는 조사와 호칭. 「김진이 25일로」, 「이훈은」, 「김진 대리」의 이름을 놓쳤다(독립 재검토). */
const AFTER_NAME = '(?:님께|님이|님은|께서|에게서|에게|한테서|한테|이|가|은|는|을|를|과|와|의|도|만|께|님|씨)(?![가-힣])'
  + '|\\s*(?:님|씨|대리|과장|차장|부장|팀장|주임|사원|기사|선임|책임|수석)(?![가-힣])';

/** 이름이 글에 사람 이름으로 나왔나. 두 글자 이름은 다른 낱말의 일부(「김진수」)가 아니어야 하고, 흔한 낱말과 같으면 더 좁게 본다. */
function nameMatcher(name: string): RegExp {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (name.length >= 3) return new RegExp(escaped, 'g');
  const plain = '$|[^가-힣\\s]|\\s+[^가-힣\\s]';
  return new RegExp(`(?<![가-힣])${escaped}(?=${COMMON_WORD.test(name) ? plain : `${plain}|${AFTER_NAME}`})`, 'g');
}

/** 글에 든 사람 이름을 ○○로 지운다. 긴 이름부터. */
export function withoutNames(text: string, names: string[]): string {
  return [...names].sort((left, right) => right.length - left.length)
    .reduce((done, name) => done.replace(nameMatcher(name), '○○'), text);
}

/** 글에 사람 이름이 하나라도 있나. */
export function namesIn(text: string, names: string[]): boolean {
  return names.some((name) => name.length >= 2 && nameMatcher(name).test(text));
}

/** 사람 이름 칸의 값, 긴 이름부터. 공지에 싣는 글에서 지운다. */
export function personNames(table: TableData): string[] {
  const found = new Set<string>();
  table.columns.forEach((column, index) => {
    const plain = column.replace(/\s+/g, '');
    if (!PERSON_COLUMN.test(plain) || /부서|팀|현장|지점/.test(plain)) return;
    table.rows.forEach((row) => {
      const value = text(row[index]);
      if (value.length >= 2 && !/^\d+$/.test(value) && !TOTAL_ROW.test(value)) found.add(value);
    });
  });
  return Array.from(found).sort((left, right) => right.length - left.length);
}

function shapeOf(table: TableData, focus: ListFocus): Shape | undefined {
  const { columns, rows } = table;
  if (rows.length < 3) return undefined;
  const cellsOf = (index: number) => rows.map((row) => row[index]);
  const kinds = columns.map((name, index) => {
    const cells = cellsOf(index);
    if (codeLike(name, cells)) return 'code';
    if (timeLike(name, cells)) return 'time';
    const filled = cells.filter((cell) => text(cell));
    return filled.length && filled.every((cell) => numberOf(cell) !== undefined) ? 'number' : 'text';
  });
  const distinct = (index: number) => new Set(cellsOf(index).map(text).filter(Boolean)).size;
  const texts = columns.map((_, index) => index).filter((index) => kinds[index] === 'text');
  // 이름 칸은 거의 다 다른 값이다. 시간 칸이 이름 자리면(월별 표) 목록이 아니라 시간 흐름이라 만들지 않는다.
  const names = texts.filter((index) => distinct(index) >= rows.length * 0.8);
  const name = names.find((index) => NAME.test(columns[index] ?? '')) ?? names[0];
  if (name === undefined) return undefined;
  const groups = texts.filter((index) => index !== name && distinct(index) >= 2 && distinct(index) <= Math.min(30, rows.length * 0.6));
  // 묶음은 묶음 칸 이름(부서, 현장, 구분 등)일 때만. 재고표의 「단위」(EA, M, L)로 묶어 단위가 다른 수를 더했다(독립 검토).
  const group = GROUPS.map((pattern) => groups.find((index) => pattern.test(columns[index] ?? '')))
    .find((index) => index !== undefined) ?? -1;
  // 값이 모두 0인 칸(대체근무일수)과 더해 봐야 뜻이 없는 칸(비율, 순번)은 수치가 아니다.
  const numbers = columns.map((_, index) => index)
    .filter((index) => kinds[index] === 'number' && !NOT_SUMMED.test(columns[index] ?? '')
      && cellsOf(index).some((cell) => (numberOf(cell) ?? 0) !== 0));
  if (!numbers.length) return undefined;
  const main = mainMeasure(numbers, columns, focus);
  const prefix = (columns[main] ?? '').replace(/\s+/g, '').slice(0, 2);
  const family = numbers.filter((index) => index !== main && (columns[index] ?? '').replace(/\s+/g, '').startsWith(prefix)).slice(0, 2);
  const measures = [...family, main].sort((left, right) => left - right);
  const label = columns[name] ?? '';
  return { name, group, main, measures, unitWord: PEOPLE.test(label) ? '명' : PLACES.test(label) ? '곳' : '건' };
}

/** 칸 이름 끝의 「명」(부서명, 현장명)은 묶음 이름에서 뗀다. */
function plainLabel(name: string): string {
  return name.length > 2 && name.endsWith('명') ? name.slice(0, -1) : name;
}

/** 수 칸 이름들이 함께 시작하는 말(「연차생성」·「연차사용일수」의 「연차」). 둘 이상이고 두 글자 이상일 때만. */
function sharedStem(names: string[]): string {
  if (names.length < 2) return '';
  const plain = names.map((one) => one.replace(/\s+/g, ''));
  let stem = plain[0] ?? '';
  plain.forEach((one) => { while (stem && !one.startsWith(stem)) stem = stem.slice(0, -1); });
  return stem.length >= 2 && plain.every((one) => one.length > stem.length) ? stem : '';
}

/**
 * 수 칸의 단위. 모르면 undefined(같은 계열 주 수치의 단위를 따른다), 단위가 칸마다 다른 수량은 빈 글자다. 건수·수량을
 * 금액보다 먼저 본다(「매출 수량 합계 24원」이 나왔다, 독립 검토).
 */
function unitOf(name: string): string | undefined {
  if (/건수$/.test(name)) return '건';
  if (/수량$/.test(name)) return '';
  if (/시간$/.test(name)) return '시간';
  if (/인원|명수/.test(name)) return '명';
  if (/일수$|일$/.test(name)) return '일';
  // 수당은 돈이다. 「연차수당」이 아래 연차 줄에 걸려 날수(일)가 됐다(독립 검토). 「…금」(연차환급금, 정산금)도 돈이다(독립 재검토).
  if (/금액|금$|원$|매출|매입|수금|미수|잔액|비용|급여|수당/.test(name)) return '원';
  // 연차·휴가의 양은 날수다. 계산이 칸 이름을 「연차 잔여」처럼 「일수」 없이 지어 단위가 빠졌다(실측 2026-10-26 15:30).
  if (/연차|휴가|대휴|휴무/.test(name)) return '일';
  return undefined;
}

/** 전체에 대한 몫을 셀 수 있는 짝(생성 대비 사용·잔여, 계획 대비 실적). */
const WHOLE = /생성|발생|계획|목표|배정|부여|^총/;
const PART = /사용|실적|완료|수금|처리|소진|잔여|잔액|^미/;

/**
 * 목록 표의 문서 표·그림·근거. 이름 칸(거의 다 다른 글자)과 0 아닌 수 칸이 있어야 만든다. 주 수치는 질문·과제·계산 요약이
 * 말한 칸이다. notice면 사람 이름을 싣지 않는다. 공지판에 붙이는 문서다.
 */
export function listReport(
  table: TableData,
  focus: ListFocus,
  /** keepNumbered: 1턴이 사용자 말대로 이름에 숫자가 붙은 계정을 남겼다. 빼지 않고 확인 줄로 낸다. */
  options: { notice?: boolean; keepNumbered?: boolean } = {},
): ListReport | undefined {
  const shape = shapeOf(table, focus);
  if (!shape) return undefined;
  const { columns } = table;
  const { name, group, main, measures, unitWord } = shape;
  // 이름이 비고 다른 칸(사원코드)이 「합계」인 줄도 합계 줄이다. 사람으로 세면 1위가 합계 줄이 되고 「미지정 145일(1명)」 묶음이
  // 생겼다(독립 검토).
  const listed = table.rows.filter((row) => !TOTAL_ROW.test(text(row[name])) && !(group >= 0 && TOTAL_ROW.test(text(row[group])))
    && !(!text(row[name]) && row.some((cell) => TOTAL_ROW.test(text(cell)))));
  if (!listed.length) return undefined;
  // 연차·휴가 목록인가. 연차수당 같은 돈 목록은 아니다(독립 검토). 시간 단위 목록은 연차 목록이다(독립 재검토).
  const leave = /연차|휴가/.test(measures.map((index) => columns[index] ?? '').join(' ')) && unitOf(columns[main] ?? '') !== '원';
  // 이름에 숫자가 붙은 사람(「2가나다」, 「가나다2」). 모든 이름에 붙었으면 이름 규칙이라 세지 않는다.
  const numbered = unitWord === '명' ? listed.filter((row) => /\d/.test(text(row[name]))) : [];
  const odd = numbered.length < listed.length ? numbered : [];
  // 연차 목록에서는 대상에서 뺀다. 같은 사람의 중복 등록이나 관리상 따로 등록한 계정이라 그 연차는 실제 연차가 아니다(사용자 확인,
  // 2026-10-27: 실측 9계정 중 8계정이 사용 0일이었다). 셋에 하나를 넘으면 이름 규칙일 수 있어 빼지 않는다(독립 검토). 빼지 않은
  // 숫자 이름과 연차가 아닌 목록(그 계정의 실적도 실제다)은 확인 줄로 낸다.
  const dropped = leave && !options.keepNumbered && odd.length * 3 <= listed.length ? odd : [];
  const rows = dropped.length ? listed.filter((row) => dropped.indexOf(row) < 0) : listed;
  const mainUnit = unitOf(columns[main] ?? '') ?? '';
  // 같은 계열 칸의 단위는 주 수치를 따른다. 「연차생성」에는 단위 낱말이 없다.
  const unit = (index: number) => unitOf(columns[index] ?? '') ?? mainUnit;
  // 칸 이름은 사람 말로 줄인다. 표 머리는 「생성·사용·잔여」, 글과 그림은 「연차 잔여」(공지 표 머리가 「연차사용일수」였다).
  const stem = sharedStem(measures.map((index) => columns[index] ?? ''));
  const head = (index: number) => {
    const raw = columns[index] ?? '';
    return stem ? raw.replace(/\s+/g, '').slice(stem.length).replace(/일수$/, '') || raw : humanLabel(raw);
  };
  const label = (index: number) => (stem ? `${stem} ${head(index)}` : humanLabel(columns[index] ?? ''));
  const mainLabel = label(main);
  const groupLabel = group >= 0 ? plainLabel(columns[group] ?? '') : '';
  const countLabel = unitWord === '명' ? '인원' : unitWord === '곳' ? `${plainLabel(columns[name] ?? '')} 수` : '건수';
  const sumOf = (list: unknown[][], index: number) => exact(list.reduce<number>((sum, row) => sum + (numberOf(row[index]) ?? 0), 0));
  const mainAt = measures.indexOf(main);
  const sums = measures.map((index) => sumOf(rows, index));
  const total = rows.length;
  const units = Array.from(new Set([unitWord, ...measures.map(unit)].filter(Boolean))).join(', ');

  const tables: ListTable[] = [];
  // 목록은 모집단을 거른 결과다(잔여가 있는 직원만). 「생성 1,216일 중 사용 40.7%」가 회사 전체 사용률로 읽혀 「대상 74명 기준」을
  // 밝히게 했다(국가직 팀장 재검토).
  const facts: string[] = [[`대상 ${shown(total)}${unitWord}`,
    ...measures.map((index, at) => `${label(index)} 합계 ${shown(sums[at] ?? 0)}${unit(index)}`)].join(', ')];
  const whole = measures.find((index) => WHOLE.test(columns[index] ?? ''));
  const parts = measures.filter((index) => index !== whole && PART.test(columns[index] ?? ''));
  if (whole !== undefined && parts.length && sums[measures.indexOf(whole)]) {
    facts.push(`대상 ${shown(total)}${unitWord}의 ${parts.map((index) => `${label(index)} 합계는 ${label(whole)} 합계의 `
      + `${percent(sums[measures.indexOf(index)] ?? 0, sums[measures.indexOf(whole)] ?? 0)}%`).join(', ')}`);
  }
  // 확인 필요 사항. 빼지 않은 숫자 이름은 중복 등록이나 다른 소속 계정일 수 있다. 연차 목록의 임원과 생성 25일 초과는 세지 않는다
  // (사용자 확인, 2026-10-27: 임원도 촉진 대상이고, 25일 초과는 창립 때부터 일한 직원이다).
  const checks: string[] = [];
  const sumAt = (list: unknown[][]) => exact(list.reduce<number>((sum, row) => sum + (numberOf(row[main]) ?? 0), 0));
  const flagged = dropped.length ? [] : odd;
  if (flagged.length) {
    const used = measures.find((index) => index !== main && USED.test(columns[index] ?? ''));
    const idle = used === undefined ? 0 : flagged.filter((row) => (numberOf(row[used]) ?? 0) === 0).length;
    checks.push(`이름에 숫자가 붙은 ${shown(flagged.length)}${unitWord}(${mainLabel} ${shown(sumAt(flagged))}${mainUnit}, `
      + `전체의 ${percent(sumAt(flagged), sums[mainAt] ?? 0)}%${used !== undefined && idle ? `, ${shown(idle)}${unitWord}은 ${label(used)} 0${unit(used)}` : ''})`
      + '은 계정 확인 필요');
  }
  // 조회한 계정 수와 합계를 앞에 붙여 1턴 명단과 잇고, 까닭은 사용자 말(「관리상 두명 등록」)대로 쓴다. 계정이 모두 빠진 묶음은
  // 그림과 표에서 사라지니 밝힌다(국가직 팀장 검토).
  const keyOf = (row: unknown[]) => text(row[group]) || '미지정';
  const gone = group >= 0 ? Array.from(new Set(dropped.map(keyOf))).filter((key) => !rows.some((row) => keyOf(row) === key)) : [];
  const goneName = gone.every((key) => key.endsWith(groupLabel)) ? gone.join(', ') : `${gone.join(', ')} ${groupLabel}`;
  const excluded = dropped.length ? `조회한 ${shown(listed.length)}개 계정(${mainLabel} ${shown(sumAt(listed))}${mainUnit}) 가운데 이름에`
    + ` 숫자가 붙은 ${shown(dropped.length)}개 계정(${shown(sumAt(dropped))}${mainUnit})은 같은 사람을 관리상 두 번 등록한 것이라`
    + ` 대상에서 제외${gone.length ? `(${topicParticle(goneName)} 계정이 모두 빠져 대상 없음)` : ''}` : undefined;
  facts.push(`1${unitWord}당 ${mainLabel} 평균 ${shown(Math.round(((sums[mainAt] ?? 0) / total) * 10) / 10)}${mainUnit}`);
  const positive = rows.filter((row) => (numberOf(row[main]) ?? 0) > 0).length;
  if (positive < total) facts.push(`${subjectParticle(mainLabel)} 0보다 큰 ${shown(positive)}${unitWord}`);

  let chart: ReportChart | undefined;
  let chartLine: string | undefined;
  if (group >= 0) {
    const buckets = new Map<string, unknown[][]>();
    // 묶음마다 배열을 새로 펼치면 큰 표(5만 행)에서 몇 초가 걸렸다(독립 검토).
    rows.forEach((row) => {
      const key = text(row[group]) || '미지정';
      const bucket = buckets.get(key);
      if (bucket) bucket.push(row);
      else buckets.set(key, [row]);
    });
    const ordered = Array.from(buckets.entries())
      .map(([key, list]) => ({ key, count: list.length, sums: measures.map((index) => sumOf(list, index)) }))
      .sort((left, right) => (right.sums[mainAt] ?? 0) - (left.sums[mainAt] ?? 0) || right.count - left.count);
    // 공지는 셋 미만인 묶음을 따로 싣지 않는다. 한두 명 부서의 줄이 곧 그 사람의 연차다(독립 검토). 합친 「그 밖」도 셋 미만이면
    // 가장 작은 부서부터 「그 밖」에 더 합쳐 셋 이상으로 만든다. 계에서 보이는 줄을 빼면 한 사람의 값이 나왔다(독립 재검토).
    // 그림도 따로 실은 부서만 그린다.
    const few = (one: { count: number }) => !!options.notice && unitWord === '명' && one.count < SMALL_GROUP;
    const visible = ordered.filter((one) => !few(one));
    const hidden = ordered.filter(few);
    const people = (list: typeof ordered) => list.reduce((sum, one) => sum + one.count, 0);
    let alone = visible.length + (hidden.length ? 1 : 0) > GROUP_ROWS ? visible.slice(0, GROUP_ROWS - 1) : visible;
    while (options.notice && alone.length && hidden.length && people([...visible.slice(alone.length), ...hidden]) < SMALL_GROUP) {
      alone = alone.slice(0, -1);
    }
    const rest = [...visible.slice(alone.length), ...hidden];
    const restRow = rest.length && people(rest) >= (options.notice ? SMALL_GROUP : 1) ? [{
      key: `그 밖 ${rest.length}개 ${groupLabel}`, count: people(rest),
      sums: measures.map((_, at) => exact(rest.reduce((sum, one) => sum + (one.sums[at] ?? 0), 0))),
    }] : [];
    const rowsShown = [...alone, ...restRow];
    const hiddenOnly = people(rowsShown) < total;
    // 따로 실을 부서가 하나도 없으면(작은 조직의 공지) 부서 표도 그림도 부서 근거도 만들지 않는다. 막대 없는 그림과
    // 「그 밖」 한 줄뿐인 표가 나갔다(독립 재검토).
    if (alone.length) {
      tables.push({
        role: 'groups',
        caption: `${groupLabel}별 ${stem ? `${stem} ` : ''}현황${units ? ` (단위: ${units}` : ''}`
          + `${hiddenOnly ? `${units ? ', ' : ' ('}${SMALL_GROUP}명 미만 ${groupLabel}는 계에만 포함` : ''}${units || hiddenOnly ? ')' : ''}`,
        table: {
          columns: [groupLabel, countLabel, ...measures.map(head)],
          rows: [...rowsShown.map((one) => [one.key, one.count, ...one.sums]), ['계', total, ...sums]],
        },
      });
      // 공지의 그림과 근거도 표에 따로 실은 부서만 쓴다. 근거의 전체 숫자에서 빼면 같은 셈이 된다.
      const named = options.notice ? alone : visible;
      const bars = named.slice(0, CHART_BARS);
      const base = named.length > CHART_BARS ? `${mainLabel} 상위 ${CHART_BARS}개 ${groupLabel}` : `${groupLabel}별 ${mainLabel}`;
      const merged = options.notice && rest.length ? `(그 밖 ${rest.length}개 ${groupLabel} 제외)` : '';
      // 공지 그림의 범주에는 인원을 붙이지 않는다. 잔여 보유자 수가 부서 인원으로 읽힌다(독립 재검토).
      chart = {
        title: `${base}${merged}`,
        categories: bars.map((one) => (options.notice ? one.key : `${one.key}(${shown(one.count)}${unitWord})`)),
        series: [{ name: mainLabel, values: bars.map((one) => one.sums[mainAt] ?? 0) }],
        ...(mainUnit ? { unit: mainUnit } : {}),
      };
      const top3 = named.slice(0, 3).reduce((sum, one) => sum + (one.sums[mainAt] ?? 0), 0);
      facts.push(`${groupLabel}별 ${mainLabel}(많은 순${merged ? `, 그 밖 ${rest.length}개 ${groupLabel}는 합쳐 셈` : ''}): `
        + `${named.slice(0, 5).map((one) => `${one.key} ${shown(one.sums[mainAt] ?? 0)}${mainUnit}(${shown(one.count)}${unitWord})`).join(', ')}`
        + (named.length > 3 ? `, 상위 3개 ${groupLabel} 합계 ${shown(exact(top3))}${mainUnit}(전체의 ${percent(top3, sums[mainAt] ?? 0)}%)` : ''));
      // 그림 장의 제목은 결론이다. 「잔여 연차일수 상위 12개 부서」라는 그림 이름만 제목에 올랐다(국가직 팀장 검토, 연차 발표자료).
      if (named.length > 3) {
        chartLine = `${mainLabel} 상위 3개 ${groupLabel}(${named.slice(0, 3).map((one) => one.key).join(', ')}) `
          + `${shown(exact(top3))}${mainUnit}, 전체의 ${percent(top3, sums[mainAt] ?? 0)}%`;
      }
    }
  }

  const ranked = rows.map((row, at) => ({ row, at, value: numberOf(row[main]) ?? 0 }))
    .sort((left, right) => right.value - left.value || left.at - right.at);
  // 끝이 동점이면 셋까지 더 싣는다(점검 그림과 같은 규칙). 그래도 동점이 이어지면 동점 묶음 앞에서 끊고 표 이름에 밝힌다. 10위
  // 16일이 7명인데 한 명만 실렸다(국가직 팀장 검토, 연차 발표자료).
  const valueAt = (index: number) => ranked[index]?.value;
  let limit = Math.min(TOP, ranked.length);
  while (limit < ranked.length && limit < TOP + 3 && valueAt(limit) === valueAt(limit - 1)) limit += 1;
  // 1위부터 동점이면(start 0) 끊을 자리가 없다. 열 명을 싣고 1위 동점 수를 밝힌다(독립 재검토: 아무 표시 없이 13명에서 끊겼다).
  let tied: { value: number; count: number; first: boolean } | undefined;
  if (limit < ranked.length && valueAt(limit) === valueAt(limit - 1)) {
    const value = valueAt(limit - 1) ?? 0;
    const start = ranked.findIndex((one) => one.value === value);
    limit = start > 0 ? start : Math.min(TOP, ranked.length);
    tied = { value, count: ranked.filter((one) => one.value === value).length, first: start === 0 };
  }
  const shownTop = ranked.slice(0, limit);
  if (!options.notice) {
    const many = total > shownTop.length;
    const cut = !tied ? '' : tied.first
      ? `1위 ${shown(tied.value)}${mainUnit} 동점 ${shown(tied.count)}${unitWord} 가운데 ${shown(limit)}${unitWord}, `
      : `${shown(tied.value)}${mainUnit} 동점 ${shown(tied.count)}${unitWord} 제외, `;
    tables.push({
      role: 'top',
      caption: `${mainLabel} ${many ? `상위 ${shownTop.length}${unitWord}` : '많은 순'} `
        + `(${cut}전체 ${shown(total)}${unitWord}${mainUnit ? `, 단위: ${mainUnit}` : ''})`,
      table: {
        columns: [columns[name] ?? '', ...(group >= 0 ? [groupLabel] : []), ...measures.map(head)],
        rows: shownTop.map(({ row }) => [text(row[name]), ...(group >= 0 ? [text(row[group])] : []),
          ...measures.map((index) => numberOf(row[index]) ?? 0)]),
      },
    });
    const topSum = exact(shownTop.reduce((sum, one) => sum + one.value, 0));
    facts.push(`${mainLabel} 상위 ${shownTop.length}${unitWord} 합계 ${shown(topSum)}${mainUnit}(전체의 ${percent(topSum, sums[mainAt] ?? 0)}%)`
      + (tied ? `, ${tied.first ? '1위' : '그다음'} ${shown(tied.value)}${mainUnit}은 ${shown(tied.count)}${unitWord} 동점` : '')
      + `, 상위 5${unitWord}: ${shownTop.slice(0, 5).map(({ row, value }) => (
        `${text(row[name])}${group >= 0 ? `(${text(row[group])})` : ''} ${shown(value)}${mainUnit}`)).join(', ')}`);
    if (!chart) {
      chart = {
        title: `${mainLabel} 상위 ${shownTop.length}${unitWord}`,
        categories: shownTop.map(({ row }) => text(row[name])),
        series: [{ name: mainLabel, values: shownTop.map(({ value }) => value) }],
        ...(mainUnit ? { unit: mainUnit } : {}),
      };
    }
  }
  const figures = [{ label: `대상 ${countLabel}`, value: `${shown(total)}${unitWord}` },
    ...measures.map((index, at) => ({ label: label(index), value: `${shown(sums[at] ?? 0)}${unit(index)}` }))].slice(0, 4);
  const confirm = flagged.length ? [`이름에 숫자가 붙은 ${shown(flagged.length)}${unitWord}`] : [];
  const executives = group >= 0 && rows.some((row) => /^임원(?:실|진)?$/.test(text(row[group])));
  return {
    tables, ...(chart ? { chart } : {}), ...(chartLine ? { chartLine } : {}), facts, figures, checks, leave, confirm,
    ...(excluded ? { excluded, excludedNames: dropped.map((row) => text(row[name])) } : {}),
    // 표에서 뺀 계정도 순위에는 든다. 1턴 답의 표 앞부분이 이 차례라 뒤로 밀면 「많은 순」이 거짓이 된다(독립 재검토).
    ranked: listed.map((row, at) => ({ row, at, value: numberOf(row[main]) ?? 0 }))
      .sort((left, right) => right.value - left.value || left.at - right.at).map((one) => one.row),
    mainLabel,
    // 「연차미사용일수」도 남은 양이다(독립 재검토: 「^미」는 칸 이름 맨 앞만 봤다).
    nameColumn: columns[name] ?? '', count: total, executives,
    remaining: /잔여|잔액|^미|미사용|미처리|미수|미결/.test((columns[main] ?? '').replace(/\s+/g, '')),
  };
}

/** 남은 양을 묻는 말인가(「촉진」, 「잔여」, 「안 쓴」). 1턴 연차 명단의 대표 직원을 잔여 많은 순으로 보일지 정한다. */
export function asksRemaining(said: string): boolean {
  return REMAINING_ASK.test(said);
}

/**
 * 연차 자료에서 같은 사람을 관리상 두 번 등록한 계정(이름에 숫자가 붙은 계정)을 계산 전에 가른다(사용자 확인, 2026-10-27: 1턴
 * 명단에서도 뺀다). 계산이 고른 표에서 빼면 「잔여 많은 10명」이 7명이 되고, 표에 없던 같은 부서 사람을 두고 「대상 없음」이라
 * 썼다(독립 검토). 사람마다 한 행인 연차 날수 자료에서, 숫자 이름이 사람의 셋에 하나 이하이고 전부가 아닐 때만 가른다. 한 사람이
 * 여러 행인 사용 내역은 그 계정의 사용이 실제일 수 있어 두고, 돈 칸(연차수당)만 있는 자료도 둔다.
 * line은 보고서 요약 끝 줄, answer는 1턴 답에 넣는 문장이다.
 */
export function duplicateAccounts(rows: Record<string, unknown>[], columns: { id: string; text?: string }[]): {
  rows: Record<string, unknown>[]; names: string[]; line: string; answer: string;
} | undefined {
  // 펼쳐 읽은 위 표 칸은 글자 끝에 줄표와 설명(「…상세는 「연차 내역」 표에 있다」)이 붙는다. 칸 이름만 본다(독립 재검토: 사원번호가
  // 연차 칸이 됐다).
  const labelOf = (column: { id: string; text?: string }) => (column.text ?? column.id).split(' — ')[0]!.replace(/\s+/g, '');
  const numeric = (id: string) => {
    const filled = rows.map((row) => text(row[id])).filter(Boolean);
    return filled.length > 0 && filled.every((value) => numberOf(value) !== undefined);
  };
  const leave = columns.filter((column) => /연차|휴가/.test(labelOf(column)) && unitOf(labelOf(column)) !== '원' && numeric(column.id));
  const person = columns.find((column) => PERSON_COLUMN.test(labelOf(column)) && !/부서|팀|현장|지점/.test(labelOf(column)));
  if (!leave.length || !person) return undefined;
  const nameOf = (row: Record<string, unknown>) => text(row[person.id]);
  const people = rows.filter((row) => nameOf(row) && !TOTAL_ROW.test(nameOf(row)));
  if (people.length < 3 || new Set(people.map(nameOf)).size < people.length * 0.8) return undefined;
  const numbered = people.filter((row) => /\d/.test(nameOf(row)));
  if (!numbered.length || numbered.length === people.length || numbered.length * 3 > people.length) return undefined;
  const dropped = new Set(numbered);
  const kept = rows.filter((row) => !dropped.has(row));
  // 뺀 계정의 잔여 날수(연차 잔여 칸을 먼저 본다). 잔여 칸이 없으면 계정 수만 쓴다.
  const rest = leave.find((column) => /연차.*잔여|잔여.*연차/.test(labelOf(column)))
    ?? leave.find((column) => /잔여|미사용/.test(labelOf(column)));
  const days = rest ? `(${humanLabel(labelOf(rest))} ${shown(exact(numbered.reduce((sum, row) => sum + (numberOf(row[rest.id]) ?? 0), 0)))}`
    + `${unitOf(labelOf(rest)) ?? '일'})` : '';
  // 계정이 모두 빠진 부서는 문서의 표와 그림에서 사라지니 밝힌다(국가직 팀장 검토).
  const group = columns.find((column) => /부서/.test(labelOf(column)) && !/코드|번호/.test(labelOf(column)))
    ?? columns.find((column) => /팀$|부문/.test(labelOf(column)));
  const groupOf = (row: Record<string, unknown>) => (group ? text(row[group.id]) : '');
  const gone = Array.from(new Set(numbered.map(groupOf).filter(Boolean)))
    .filter((key) => !kept.some((row) => nameOf(row) && groupOf(row) === key));
  const groupLabel = group ? plainLabel(labelOf(group)) : '';
  const goneName = gone.every((key) => key.endsWith(groupLabel)) ? gone.join(', ') : `${gone.join(', ')} ${groupLabel}`;
  const head = `이름에 숫자가 붙은 ${shown(numbered.length)}개 계정${days}은 같은 사람을 관리상 두 번 등록한 것이라 대상에서`;
  const names = numbered.map(nameOf);
  const named = `${names.slice(0, 10).join(', ')}${names.length > 10 ? ` 외 ${shown(names.length - 10)}개 계정` : ''}`;
  return {
    rows: kept,
    names,
    line: `${head} 제외${gone.length ? `(${topicParticle(goneName)} 계정이 모두 빠져 대상 없음)` : ''}`,
    answer: `${head} 뺐습니다.${gone.length ? ` ${topicParticle(goneName)} 계정이 모두 빠져 대상이 없습니다.` : ''} 뺀 계정은 ${named}입니다.`,
  };
}
